import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { downloadMyPayslip, getFlexBalance, getFlexTransactions, getMe, getPayslipHistory, getVacationBalance, getVacationTransactions } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { TrendingUp, History, Palmtree, ChevronDown, ChevronUp, Download, FileText, AlertCircle } from 'lucide-react';
import { getCappedHours } from '../../lib/format-utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

export default function ProfilePage() {
    const { t, i18n } = useTranslation();
    const { user, updateUser: updateAuthUser } = useAuth();
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [flexTransactions, setFlexTransactions] = useState([]);
    const [vacationBalance, setVacationBalance] = useState(null);
    const [vacationTransactions, setVacationTransactions] = useState([]);
    const [flexLimits, setFlexLimits] = useState({ min: -40, max: 80 });

    // Pay slip history state
    const [payslips, setPayslips] = useState([]);
    const [payslipsLoading, setPayslipsLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [meRes, flexRes, vacBalRes, vacTransRes, payslipRes, flexBalRes] = await Promise.all([
                getMe(),
                getFlexTransactions(),
                getVacationBalance(),
                getVacationTransactions(),
                getPayslipHistory().catch(() => ({ data: { pay_slips: [] } })),
                getFlexBalance().catch(() => ({ data: { balance: 0, flex_limit_min: -40, flex_limit_max: 80 } })),
            ]);
            updateAuthUser(meRes.data);
            setFlexTransactions(flexRes.data);
            setVacationBalance(vacBalRes.data);
            setVacationTransactions(vacTransRes.data);
            setPayslips(payslipRes.data.pay_slips);
            if (flexBalRes?.data) {
                setFlexLimits({
                    min: flexBalRes.data.flex_limit_min ?? -40,
                    max: flexBalRes.data.flex_limit_max ?? 80,
                });
            }
        } catch (error) {
            console.error('Failed to fetch records data:', error);
        } finally {
            setPayslipsLoading(false);
        }
    };

    const handleDownloadPayslip = async (year, month) => {
        const key = `${year}-${month}`;
        setDownloadingId(key);
        try {
            const res = await downloadMyPayslip(year, month);
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `payslip_${year}-${String(month).padStart(2, '0')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download pay slip');
        } finally {
            setDownloadingId(null);
        }
    };

    const minLim = flexLimits.min || -40;
    const maxLim = flexLimits.max || 80;
    const minAbs = Math.abs(minLim);
    const totalSpan = maxLim + minAbs || 120;
    const zeroPosPercent = (minAbs / totalSpan) * 100;
    const currentBal = user?.flex_balance || 0;
    let barLeft = `${zeroPosPercent}%`;
    let barWidth = '0%';
    if (currentBal >= 0) {
        barLeft = `${zeroPosPercent}%`;
        barWidth = `${Math.min(100 - zeroPosPercent, (currentBal / (maxLim || 1)) * (100 - zeroPosPercent))}%`;
    } else {
        const fraction = Math.min(1, Math.abs(currentBal) / (minAbs || 1));
        const widthPct = fraction * zeroPosPercent;
        barLeft = `${zeroPosPercent - widthPct}%`;
        barWidth = `${widthPct}%`;
    }

    return (
        <div className="space-y-8" data-testid="profile-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-4xl font-bold tracking-tight">{t('records.page_title')}</h1>
                <p className="text-muted-foreground mt-1">{t('records.page_subtitle')}</p>
            </div>

            {/* Balances Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Flex Balance */}
                <Card className="border border-border shadow-none">
                    <CardHeader className="border-b border-border">
                        <CardTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" strokeWidth={1.5} />
                            {t('profile.flex_balance_title')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                                    {t('profile.current_balance_label')}
                                </p>
                                <p className={`font-heading text-5xl font-black ${user?.flex_balance >= 0 ? 'flex-positive' : 'flex-negative'}`}>
                                    {user?.flex_balance > 0 ? '+' : ''}{user?.flex_balance || 0}h
                                </p>
                            </div>
                            <div className="text-right">
                                <div className="flex gap-8">
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('profile.flex_max')}</p>
                                        <p className="font-mono text-xl flex-positive">+{flexLimits.max}h</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('profile.flex_min')}</p>
                                        <p className="font-mono text-xl flex-negative">{flexLimits.min}h</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="h-4 bg-muted relative">
                            <div
                                className="absolute top-0 bottom-0 w-0.5 bg-border z-10"
                                style={{ left: `${zeroPosPercent}%` }}
                            />
                            <div
                                className={`absolute top-0 bottom-0 ${currentBal >= 0 ? 'bg-green-500' : 'bg-destructive'}`}
                                style={{
                                    left: barLeft,
                                    width: barWidth
                                }}
                            />
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                            <span>{flexLimits.min}h</span>
                            <span>0h</span>
                            <span>+{flexLimits.max}h</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Vacation Balance */}
                <Card className="border border-border shadow-none">
                    <CardHeader className="border-b border-border">
                        <CardTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
                            <Palmtree className="w-5 h-5" strokeWidth={1.5} />
                            {t('profile.vacation_days_title')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                                    {t('profile.available_days_label')}
                                </p>
                                <p className="font-heading text-5xl font-black flex-positive">
                                    {vacationBalance?.total_available || 0}
                                </p>
                            </div>
                            <div className="text-right space-y-2">
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('profile.earned_label')}</p>
                                    <p className="font-mono text-xl">{vacationBalance?.vacation_days || 0} {t('common.days_unit')}</p>
                                </div>
                                {vacationBalance?.years_employed < 5 && (
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('profile.advance_available_label')}</p>
                                        <p className="font-mono text-xl">
                                            {(vacationBalance?.advance_vacation_days || 0) - (vacationBalance?.advance_vacation_used || 0)} {t('common.days_unit')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-3 bg-muted text-sm">
                            <p className="text-muted-foreground">
                                <strong>{t('profile.employment_label')}</strong> {vacationBalance?.years_employed || 0} {t('profile.years_label')}
                                {vacationBalance?.years_employed >= 5 && (
                                    <span className="ml-2 text-green-600">{t('profile.advance_cleared')}</span>
                                )}
                            </p>
                            {vacationBalance?.years_employed < 5 && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('profile.advance_offset_notice')}
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pay Slips History */}
            <Card className="border border-border shadow-none">
                <CardHeader className="border-b border-border">
                    <CardTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
                        <FileText className="w-5 h-5" strokeWidth={1.5} />
                        {t('records.payslips_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                    {payslipsLoading ? (
                        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                    ) : payslips.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('records.payslips_empty')}</p>
                    ) : (
                        <div className="space-y-2">
                            {payslips.map(ps => {
                                const key = `${ps.year}-${ps.month}`;
                                const label = new Date(ps.year, ps.month - 1, 1)
                                    .toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
                                return (
                                    <div key={ps.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                                        <div>
                                            <p className="text-sm font-medium">{label}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {t('records.payslip_generated', {
                                                    date: new Date(ps.generated_at).toLocaleDateString(i18n.language)
                                                })}
                                                {ps.revision > 1 && ` · v${ps.revision}`}
                                            </p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="uppercase tracking-wider text-xs"
                                            disabled={downloadingId === key}
                                            onClick={() => handleDownloadPayslip(ps.year, ps.month)}
                                        >
                                            <Download className="w-3 h-3 mr-1.5" />
                                            {downloadingId === key ? t('profile.payslip_downloading') : t('common.download', 'Download')}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* History Section — tabbed, collapsed by default */}
            <Card className="border border-border shadow-none">
                <CardHeader
                    className="border-b border-border cursor-pointer"
                    onClick={() => setHistoryExpanded(prev => !prev)}
                >
                    <div className="flex items-center justify-between">
                        <CardTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
                            <History className="w-5 h-5" strokeWidth={1.5} />
                            {t('profile.flex_history_title')}
                        </CardTitle>
                        {historyExpanded
                            ? <ChevronUp className="w-5 h-5 text-muted-foreground" />
                            : <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        }
                    </div>
                </CardHeader>
                {historyExpanded && (
                    <CardContent className="pt-4">
                        <Tabs defaultValue="flex">
                            <TabsList className="mb-4">
                                <TabsTrigger value="flex">
                                    <TrendingUp className="w-4 h-4 mr-2" />
                                    {t('profile.flex_history_title')}
                                </TabsTrigger>
                                <TabsTrigger value="vacation">
                                    <Palmtree className="w-4 h-4 mr-2" />
                                    {t('profile.vacation_history_title')}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="flex">
                                {flexTransactions.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground">
                                        <History className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                        <p>{t('profile.no_flex_transactions')}</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('profile.col_date')}</th>
                                                    <th>{t('profile.col_change')}</th>
                                                    <th>{t('profile.col_reason')}</th>
                                                    <th>{t('profile.col_balance_after')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {flexTransactions.map((tx) => {
                                                    const capped = getCappedHours(tx);
                                                    return (
                                                        <tr key={tx.id}>
                                                            <td className="font-mono">{tx.date}</td>
                                                            <td className={`font-mono font-bold ${tx.hours >= 0 ? 'flex-positive' : 'flex-negative'}`}>
                                                                <div>{tx.hours > 0 ? '+' : ''}{tx.hours}h</div>
                                                                {capped ? (
                                                                    <div className="inline-flex items-center gap-1 text-[11px] font-sans font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 px-1.5 py-0.5 rounded mt-1 whitespace-nowrap">
                                                                        <AlertCircle className="w-3 h-3 shrink-0" />
                                                                        <span>{t('profile.flex_not_added', { count: capped })}</span>
                                                                    </div>
                                                                ) : null}
                                                            </td>
                                                            <td>{tx.reason}</td>
                                                            <td className="font-mono">{tx.balance_after}h</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="vacation">
                                {vacationTransactions.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground">
                                        <Palmtree className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                        <p>{t('profile.no_vacation_transactions')}</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('profile.col_date')}</th>
                                                    <th>{t('profile.col_days')}</th>
                                                    <th>{t('profile.col_type')}</th>
                                                    <th>{t('profile.col_reason')}</th>
                                                    <th>{t('profile.col_balance_after')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {vacationTransactions.map((tx) => (
                                                    <tr key={tx.id}>
                                                        <td className="font-mono">{tx.date}</td>
                                                        <td className={`font-mono font-bold ${tx.days >= 0 ? 'flex-positive' : 'flex-negative'}`}>
                                                            {tx.days > 0 ? '+' : ''}{tx.days}d
                                                        </td>
                                                        <td>
                                                            <span className={`text-xs uppercase tracking-wider px-2 py-1 ${
                                                                tx.transaction_type === 'earned' ? 'bg-green-100 text-green-800' :
                                                                tx.transaction_type === 'advance_used' ? 'bg-orange-100 text-orange-800' :
                                                                'bg-muted'
                                                            }`}>
                                                                {tx.transaction_type}
                                                            </span>
                                                        </td>
                                                        <td>{tx.reason}</td>
                                                        <td className="font-mono">{tx.vacation_balance_after}d</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                )}
            </Card>
        </div>
    );
}
